import { integrationTesting, tryParse } from '@ps/web-core-utils';
import type { Client, StompHeaders } from '@stomp/stompjs';
import { camelCase, has, mapKeys, set, unset } from 'lodash';
import type { Observer } from 'rxjs';
import { Observable } from 'rxjs';
import { filter, finalize, share, switchMap } from 'rxjs/operators';
import type { Primitive } from 'utility-types';

import { State, connect } from '@swf/core/api/connect';
import type { Message } from '@swf/core/types/Notification';

export type { Message } from '@swf/core/types/Notification';

const subscriptions: Record<string, Observable<Message>> = {};

const subscription = (client: Client, destination: string, headers?: Message['headers']) =>
  new Observable((messages: Observer<Message>) => {
    const stompSub = client.subscribe(
      destination,
      (message) =>
        messages.next({
          headers: Object.keys(message.headers).reduce(
            (memo, headerName) => {
              memo[camelCase(headerName)] = tryParse(message.headers[headerName]);
              return memo;
            },
            {} as Record<string, Primitive>
          ),
          body: tryParse(message.body)
        }),
      mapKeys({ ...(headers as StompHeaders) }, (value, key) => key.toLowerCase())
    );

    if (integrationTesting) set(window, ['__STARS_SUBSCRIPTIONS__', destination], stompSub.id);

    return () => {
      if (client.connected && has(client, `subscriptions.${stompSub.id}`)) {
        stompSub.unsubscribe();
        if (integrationTesting) unset(window, ['__STARS_SUBSCRIPTIONS__', destination]);
      }
    };
  });

export const subscribe = <T = Message>(
  destination: string,
  connection: ReturnType<typeof connect> = connect()
): Observable<T> => {
  if (!subscriptions[destination]) {
    subscriptions[destination] = connection.pipe(
      filter(({ state }) => state === State.CONNECTED),
      switchMap(({ client, config }) =>
        subscription(client as Client, destination, config?.getSubscriptionHeaders?.())
      ),
      finalize(() => delete subscriptions[destination]),
      share()
    );
  }

  return subscriptions[destination] as Observable<T>;
};
